const toISO = (d: Date) => d.toLocaleDateString("sv-SE");

// The client-side reporting boundary (Media Performance issue 34). Reporting is
// complete-days-only, so no preset or custom range may end after yesterday.
// This is the browser-local yesterday — the server enforces the authoritative
// UTC boundary on every read and enqueue, so a local/UTC edge (e.g. 00:00–02:00
// CEST) can at worst request one column the server renders as unmeasured.
export function latestSelectableReportingDay(now: Date = new Date()): string {
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  return toISO(yesterday);
}

// Clamp a user-chosen date at the reporting boundary; blank stays blank so an
// unset custom field keeps meaning "unset".
export function clampSelectableReportingDay(value: string): string {
  if (!value) return value;
  const boundary = latestSelectableReportingDay();
  return value > boundary ? boundary : value;
}

// Get date range presets
// Week boundaries are Mon-Sun. Every preset ends at the reporting boundary
// (yesterday, issue 34): the "this …" presets are anchored on YESTERDAY, so on
// a Monday / the 1st / Jan 1 they resolve to the latest complete week / month /
// year instead of an empty or today-including range. `now` is injectable for
// deterministic tests.
export function getDatePreset(
  preset: string,
  now: Date = new Date(),
): { start: string; end: string } {
  const today = new Date(now);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Monday of the week containing yesterday (Mon=1, Sun=0 mapped to 7).
  const dayOfWeek = yesterday.getDay() || 7; // Sun=0 → 7
  const thisMonday = new Date(yesterday);
  thisMonday.setDate(yesterday.getDate() - (dayOfWeek - 1));

  const presets: Record<string, () => { start: string; end: string }> = {
    yesterday: () => ({ start: toISO(yesterday), end: toISO(yesterday) }),
    thisWeek: () => ({ start: toISO(thisMonday), end: toISO(yesterday) }),
    lastWeek: () => {
      const lastMonday = new Date(thisMonday);
      lastMonday.setDate(thisMonday.getDate() - 7);
      const lastSunday = new Date(thisMonday);
      lastSunday.setDate(thisMonday.getDate() - 1);
      return { start: toISO(lastMonday), end: toISO(lastSunday) };
    },
    last7: () => {
      const start = new Date(yesterday);
      start.setDate(yesterday.getDate() - 6);
      return { start: toISO(start), end: toISO(yesterday) };
    },
    last30: () => {
      const start = new Date(yesterday);
      start.setDate(yesterday.getDate() - 29);
      return { start: toISO(start), end: toISO(yesterday) };
    },
    thisMonth: () => {
      const start = new Date(yesterday.getFullYear(), yesterday.getMonth(), 1);
      return { start: toISO(start), end: toISO(yesterday) };
    },
    lastMonth: () => {
      const start = new Date(yesterday.getFullYear(), yesterday.getMonth() - 1, 1);
      const end = new Date(yesterday.getFullYear(), yesterday.getMonth(), 0); // last day of prev month
      return { start: toISO(start), end: toISO(end) };
    },
    ytd: () => {
      const start = new Date(yesterday.getFullYear(), 0, 1);
      return { start: toISO(start), end: toISO(yesterday) };
    },
  };

  return (presets[preset] ?? presets.yesterday)();
}
